import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { PlanStepPage } from '../../../services/PlanStepPage';
import { DeliverableStepComponent } from '../deliverable-step.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PlanStep } from '../../../services/PlanStep';
import { IAnnualPlanPriority } from '../../../services/IAnnualPlanPriority';
import { IDeliverableHeadings } from '../../../services/IDeliverableHeadings';
import { PriorityService } from '../../../services/priority.service';
import { StrategiesDataStoreService } from '../../../../shared/services/strategies-data-store.service';
import { StrtHasFlags } from '../../../../shared/models/ui/StrtHasFlags';
import {ResourceService} from '../../../services/resource.service';

@Component({
  selector: 'app-annual-plan',
  templateUrl: './annual-plan.component.html',
  styleUrls: ['./annual-plan.component.scss']
})
export class AnnualPlanComponent extends DeliverableStepComponent implements OnInit {
  @Input() headings: IDeliverableHeadings;
  @Input() strategyId: number;
  @Output() requestAnnualPlanPriorities = new EventEmitter<{startDate: Date, endDate?: Date}>();
  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();
  private _hasInitiatives = false;
  private _annualPlanForm: FormGroup;
  private _priorities: IAnnualPlanPriority[] = [];
  private _groupedPriorities: IAnnualPlanPriority[][] = [];
  private formTouched = false;
  private formSubmitted = false;
  private formReturned = false;
  startDate?: Date;
  endDate?: Date;
  preventCalendarBlur = false;

  constructor(private formBuilder: FormBuilder,
              protected cdr: ChangeDetectorRef,
              private _priorityService: PriorityService,
              private _strtDataStore: StrategiesDataStoreService,
              _resourceService: ResourceService) {
    super(PlanCoordinate.stepPageBy(PlanStepPage.AnnualPlan), cdr, _resourceService);
    this.createForm();
  }

  ngOnInit() {
    this.formTouched = false;
    this.formSubmitted = false;
    this.formReturned = false;
    this._strtDataStore.hasFlags$.subscribe((hasFlags: StrtHasFlags) => {
      this._hasInitiatives = hasFlags.hasInitiatives;
      if (this.annualPlanForm) {
        if (this.startDateField) {
          this._hasInitiatives ? this.startDateField.enable() : this.startDateField.disable();
        }
        if (this.endDateField) {
          this._hasInitiatives ? this.endDateField.enable() : this.endDateField.disable();
        }
      }
    });
    this._strtDataStore.AIPPriorities$.subscribe((priorities: IAnnualPlanPriority[]) => {
      this._priorities = priorities;
        this._groupedPriorities = this.groupByPriorityNumber(this._priorities);
        if (this.formSubmitted) {
          this.formReturned = true;
        }
    });
    this.setupInitialView();
  }

  get annualPlanForm() {
    return this._annualPlanForm;
  }

  get startDateField() {
    return this._annualPlanForm.get('startDate');
  }

  get endDateField() {
    return this._annualPlanForm.get('endDate');
  }

  get groupedPriorities() {
    return this._groupedPriorities;
  }

  private createForm() {
    const validatorArr = [
      Validators.required
    ];
    this._annualPlanForm = this.formBuilder.group({
      startDate: [{value: this.startDate, disabled: true}, validatorArr],
      endDate: [{value: this.endDate, disabled: true}, []]
    });
  }

  get hasEndDateWithNoStartDateAndTouched() {
    return this.endDate && !this.startDate && (this.formTouched || this.formSubmitted);
  }

  get hasRequiredFields() {
    return this.startDate && this.formTouched && (!this.isEndDateBeforeStartDate);
  }

  get hasPriorities() {
    return this._groupedPriorities.length > 0;
  }

  get isEndDateBeforeStartDate() {
    const startDate = this.startDateField.value,
      endDate = this.endDateField.value;
    return startDate !== null && startDate !== '' && endDate !== null && endDate !== '' && endDate < startDate;
  }

  get hasNoPrioritiesAndSubmitted() {
    return this.formSubmitted && this.formReturned && !this.hasEndDateWithNoStartDateAndTouched &&
      (this._groupedPriorities.length === 0 || !this.hasAnyInitiativesInGroupedPriorities(this._groupedPriorities));
  }

  reset() {
    this.annualPlanForm.reset();
    this.formTouched = false;
    this.formSubmitted = false;
    this.startDate = undefined;
    this.endDate = undefined;
    this._priorities = [];
    this._groupedPriorities = [];
  }

  onViewAnnualPlan() {
    if (this.hasRequiredFields) {
      this._priorityService.getAnnualPlanPriorities(this.strategyId, this.startDate, this.endDate);
      this.formSubmitted = true;
      this.formReturned = false;
    }
  }

  setupInitialView() {
    this.startDate = undefined;
    this.endDate = undefined;
    this._priorityService.getAnnualPlanPriorities(this.strategyId, this.startDate, this.endDate);
    this.formSubmitted = true;
    this.formReturned = false;
  }

  onPlanCoordinateChangeCreatingObjectivesAndCriticalInitiatives() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.CREATE_OBJECTIVES_AND_CRITICAL_INITIATIVES));
  }

  onPlanCoordinateChangeIdentifyOutcomesAndKeyMeasures() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.IDENTIFY_OUTCOMES_AND_KEY_MEASURES));
  }

  onPlanCoordinateChangeDevelopAnnualPlan() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.DEVELOP_THE_ANNUAL_PLAN));
  }

  groupByPriorityNumber(annualPlanPriorities: IAnnualPlanPriority[]): IAnnualPlanPriority[][] {
    const reformatted = [];
    for (const priority of annualPlanPriorities) {
      if (!reformatted[priority.priorityNumber - 1]) {
        reformatted[priority.priorityNumber - 1] = [];
      }
      reformatted[priority.priorityNumber - 1].push(priority);
    }
    return reformatted;
  }

  priorityColorFromGroup(priorityGroup: IAnnualPlanPriority[]) {
    if (priorityGroup && priorityGroup.length > 0) {
      return priorityGroup[0].priorityColor;
    } else {
      return 'transparent';
    }
  }

  priorityNameFromGroup(priorityGroup: IAnnualPlanPriority[]) {
    if (priorityGroup && priorityGroup.length > 0) {
      return priorityGroup[0].priority;
    } else {
      return '';
    }
  }

  priorityObjectiveFromGroup(priorityGroup: IAnnualPlanPriority[]) {
    if (priorityGroup && priorityGroup.length > 0) {
      return priorityGroup[0].objective;
    } else {
      return '';
    }
  }

  hasInitiativesInPriorityGroup(priorityGroup: IAnnualPlanPriority[]) {
    let output = false;
    if (!priorityGroup) {
      return output;
    }
    priorityGroup.some((priority: IAnnualPlanPriority) => {
      output = output ? output : priority.initiatives.length > 0;
      return output;
    });
    return output;
  }

  hasAnyInitiativesInGroupedPriorities(groupedPriorities: IAnnualPlanPriority[][]) {
    let output = false;
    groupedPriorities.some((priorityGroup: IAnnualPlanPriority[]) => {
        output = output ? output : this.hasInitiativesInPriorityGroup(priorityGroup);
        return output;
    });
    return output;
  }

  resetCalendarEventBlocker() {
    this.preventCalendarBlur = true;
  }

  allowCalendarBlurEvent() {
    this.preventCalendarBlur = false;
  }

  /**
   * Event handler for start date blur or input events
   * @param event
   */
  applyStartDate(event) {
    if (event && event.type && event.type === 'blur' && this.preventCalendarBlur) {
      // Allows us to prevent executing this code in an edge caused by p-calendar
      // The edge case is the onBlur event being fired when a user clicks a date
      // The onBlur gets fired before the select event, which ends up clearing out the date
      return;
    }
    this.startDate = this.startDateField.value;
    this.formTouched = true;
  }

  /**
   * Event handler for end date blur or input events
   * @param event
   */
  applyEndDate(event) {
    if (event && event.type && event.type === 'blur' && this.preventCalendarBlur) {
      // Allows us to prevent executing this code in an edge caused by p-calendar
      // The edge case is the onBlur event being fired when a user clicks a date
      // The onBlur gets fired before the select event, which ends up clearing out the date
      return;
    }
    this.endDate = this.endDateField.value;
    this.formTouched = true;
  }

}
